/**
 * React access to the store. Every hook re-renders only when what it selects changes
 * (by reference: the store replaces records, it never mutates them).
 *
 *   const store = useStore()                 // mutations: store.updateSection(...)
 *   const { catalog } = useCatalog()
 *   const trips = useTrips()
 *   const trip = useTrip(id)
 *   const hit = useSection(tripId, sectionId) // { trip, section } | undefined
 *   const { settings, resolved, update } = useSettings()
 */
import type { ReactNode } from 'react'
import { createContext, createElement, useContext, useMemo, useSyncExternalStore } from 'react'
import type { Catalog, Section, Trip } from '@resupport/core'
import { timeOf } from '../engine'
import type { ResolvedSettings, Settings } from './settings'
import type { StoreState } from './store'
import { Store } from './store'
import type { ScratchBasket, SyncMeta, UserRating } from './types'

const StoreContext = createContext<Store | null>(null)

export function StoreProvider({ store, children }: { store: Store; children: ReactNode }) {
  return createElement(StoreContext.Provider, { value: store }, children)
}

export function useStore(): Store {
  const store = useContext(StoreContext)
  if (!store) throw new Error('useStore() outside <StoreProvider>')
  return store
}

/** Subscribe to one slice of the snapshot. The selector must return stable references. */
export function useStoreSelector<T>(select: (s: StoreState) => T): T {
  const store = useStore()
  return useSyncExternalStore(
    store.subscribe,
    () => select(store.getState()),
    () => select(store.getState()),
  )
}

export function useStoreStatus(): { ready: boolean; persistent: boolean; error: Error | null } {
  const ready = useStoreSelector((s) => s.ready)
  const persistent = useStoreSelector((s) => s.persistent)
  const error = useStoreSelector((s) => s.error)
  return { ready, persistent, error }
}

export interface CatalogView {
  /** Bundled data overlaid by the person's own foods, recipes and ratings. */
  catalog: Catalog
  ready: boolean
}

export function useCatalog(): CatalogView {
  const store = useStore()
  const catalog = useSyncExternalStore(
    store.subscribe,
    () => store.catalog(),
    () => store.catalog(),
  )
  const ready = useStoreSelector((s) => s.ready)
  return { catalog, ready }
}

/** Live (not deleted) trips, most recently changed first. */
export function useTrips(): Trip[] {
  const trips = useStoreSelector((s) => s.trips)
  return useMemo(
    () =>
      [...trips.values()]
        .filter((t) => !t.deleted)
        .sort((a, b) => timeOf(b.updated_at) - timeOf(a.updated_at)),
    [trips],
  )
}

export function useTrip(id: string | undefined): Trip | undefined {
  return useStoreSelector((s) => {
    const t = id ? s.trips.get(id) : undefined
    return t && !t.deleted ? t : undefined
  })
}

export function useSection(
  tripId: string | undefined,
  sectionId: string | undefined,
): { trip: Trip; section: Section } | undefined {
  const trip = useTrip(tripId)
  return useMemo(() => {
    const section = trip?.sections.find((s) => s.id === sectionId)
    return trip && section ? { trip, section } : undefined
  }, [trip, sectionId])
}

/**
 * The section being shopped right now, if exactly one is: status 'shopping', most recently
 * touched first. `#/shop` opens it; with none it opens the scratch basket.
 */
export function useActiveShopping(): { trip: Trip; section: Section } | undefined {
  const trips = useTrips()
  return useMemo(() => {
    let best: { trip: Trip; section: Section } | undefined
    for (const trip of trips) {
      for (const section of trip.sections) {
        if (section.status !== 'shopping') continue
        if (!best || timeOf(section.updated_at) > timeOf(best.section.updated_at)) {
          best = { trip, section }
        }
      }
    }
    return best
  }, [trips])
}

export interface SettingsView {
  settings: Settings
  /** What the settings come to: energy unit, currency, region record. Read this one. */
  resolved: ResolvedSettings
  update: Store['updateSettings']
}

export function useSettings(): SettingsView {
  const store = useStore()
  const settings = useStoreSelector((s) => s.settings)
  const resolved = useSyncExternalStore(
    store.subscribe,
    () => store.resolvedSettings(),
    () => store.resolvedSettings(),
  )
  return useMemo(
    () => ({ settings, resolved, update: (p) => store.updateSettings(p) }),
    [settings, resolved, store],
  )
}

export function useScratchBasket(): ScratchBasket {
  return useStoreSelector((s) => s.basket)
}

/** This device's own availability ratings (live ones). */
export function useUserRatings(): UserRating[] {
  const ratings = useStoreSelector((s) => s.ratings)
  return useMemo(() => [...ratings.values()].filter((r) => !r.deleted), [ratings])
}

export function useSyncMeta(): SyncMeta {
  return useStoreSelector((s) => s.sync)
}

export interface FavouritesView {
  /** Starred food ids. */
  foods: ReadonlySet<string>
  /** Starred recipe ids. */
  recipes: ReadonlySet<string>
}

const EMPTY_FAVOURITES: FavouritesView = { foods: new Set(), recipes: new Set() }

/** Starred foods and recipes (live ones). Toggle with `store.toggleFavourite`. */
export function useFavourites(): FavouritesView {
  const favourites = useStoreSelector((s) => s.favourites)
  return useMemo(() => {
    if (favourites.size === 0) return EMPTY_FAVOURITES
    const foods = new Set<string>()
    const recipes = new Set<string>()
    for (const f of favourites.values()) {
      if (f.deleted) continue
      ;(f.target === 'food' ? foods : recipes).add(f.ref)
    }
    return { foods, recipes }
  }, [favourites])
}
